/**
 * dailyIntelligence.ts — placeholder Daily Intelligence™ generation.
 *
 * Builds the "what should I know today?" feed from provider snapshots. Today
 * those snapshots are deterministic placeholders; when real providers are
 * registered, these suggestions become live automatically — the logic below
 * never hardcodes a suggestion, it derives every line from provider data.
 *
 * Pure orchestration: no React, no storage, no direct network.
 */

import type { DailySuggestion, IntelligenceContext } from './types';
import type { ProviderRegistry } from './providers/ProviderRegistry';
import { seededRng, pick, localDayIndex } from './providers/mockData';

/** Format a 24h hour as "4 PM". */
function hourLabel(hour: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12} ${hour < 12 ? 'AM' : 'PM'}`;
}

export async function buildDailySuggestions(
  ctx: IntelligenceContext,
  registry: ProviderRegistry,
): Promise<DailySuggestion[]> {
  const { location, now } = ctx;
  const suggestions: DailySuggestion[] = [];

  const [weather, internet, fx] = await Promise.all([
    registry.get('weather').getSnapshot(location, now),
    registry.get('internetQuality').getSnapshot(location, now),
    registry.get('exchangeRate').getSnapshot(location, now),
  ]);

  // ── Weather ──────────────────────────────────────────────────────────────────
  if (weather.rainStartHour != null) {
    suggestions.push({
      id: `daily:${location.locationId}:weather-rain`,
      kind: 'weather',
      icon: 'cloud-rain',
      text: `Rain expected around ${hourLabel(weather.rainStartHour)} today.`,
      detail: `High ${weather.highC}°C · ${weather.humidityPct}% humidity`,
      priority: 80,
    });
  } else {
    suggestions.push({
      id: `daily:${location.locationId}:weather-clear`,
      kind: 'weather',
      icon: 'sun',
      text: `Clear skies expected — high of ${weather.highC}°C.`,
      priority: 45,
    });
  }

  // ── Internet ─────────────────────────────────────────────────────────────────
  if (internet.todayVsNormal === 'better') {
    suggestions.push({
      id: `daily:${location.locationId}:net-better`,
      kind: 'internet',
      icon: 'wifi',
      text: 'Internet speeds are excellent today.',
      detail: `~${internet.avgDownloadMbps} Mbps down · ${internet.reliabilityPct}% reliability`,
      priority: ctx.profile.remoteWorker ? 75 : 55,
    });
  } else if (internet.todayVsNormal === 'worse') {
    suggestions.push({
      id: `daily:${location.locationId}:net-worse`,
      kind: 'internet',
      icon: 'wifi-off',
      text: 'Internet is slower than normal today — plan heavy uploads accordingly.',
      detail: `~${internet.avgDownloadMbps} Mbps down`,
      priority: ctx.profile.remoteWorker ? 85 : 50,
    });
  }

  // ── Currency ─────────────────────────────────────────────────────────────────
  if (fx.trend === 'improved') {
    suggestions.push({
      id: `daily:${location.locationId}:fx`,
      kind: 'currency',
      icon: 'trending-up',
      text: `Currency exchange improved ${Math.abs(fx.changePct)}% in your favour today.`,
      priority: 60,
    });
  }

  // ── Coworking pulse (derived from a scored sample, not hardcoded) ────────────
  if (ctx.profile.needsCoworking || ctx.profile.remoteWorker) {
    const dayRng = seededRng(`${location.locationId}:cowork-pulse:${localDayIndex(location, now)}`);
    if (dayRng() < 0.4) {
      suggestions.push({
        id: `daily:${location.locationId}:cowork`,
        kind: 'coworking',
        icon: 'laptop',
        text: 'Coworking spaces are quieter than normal today.',
        priority: 58,
      });
    }
  }

  // ── Neighborhood of the day (rotates through generated districts) ───────────
  const hoods = await registry.get('places').getPlaces({ location, category: 'neighborhood', limit: 6 });
  if (hoods.length > 0) {
    const dayRng = seededRng(`${location.locationId}:hood-of-day:${localDayIndex(location, now)}`);
    const hood = pick(dayRng, hoods);
    suggestions.push({
      id: `daily:${location.locationId}:hood`,
      kind: 'neighborhood',
      icon: 'map-pin',
      text: `Recommended neighborhood today: ${hood.title}.`,
      detail: hood.tags.slice(0, 3).join(' · ') || undefined,
      priority: 50,
    });
  }

  // ── Safety (only when the advisory is above normal) ─────────────────────────
  const advisory = await registry.get('travelAdvisory').getAdvisory(location);
  if (advisory.level >= 2) {
    suggestions.push({
      id: `daily:${location.locationId}:advisory`,
      kind: 'safety',
      icon: 'shield-alert',
      text: advisory.summary,
      priority: 70 + advisory.level * 5,
    });
  }

  return suggestions.sort((a, b) => b.priority - a.priority);
}
